import { useMutation } from '@apollo/client';
import KeyIcon from '@assets/svgs/key.svg';
import { InputOTP } from '@components/InputOTP';
import PasswordInput from '@components/Inputs/PasswordInput';
import { PageHead } from '@components/PageHead';
import { RESEND_RESET_PASSWORD_CODE, UPDATE_PASSWORD_FORGOT } from '@gql/auth';
import { Box, Button, Typography } from '@mui/material';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './index.css';

export const ResetPasswordPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();

    const email = searchParams.get('email') || '';
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordsMatch, setPasswordsMatch] = useState(true);

    const [updatePasswordForgot, { loading: resettingPassword }] = useMutation(UPDATE_PASSWORD_FORGOT, {
        onCompleted: (data) => {
            if (data?.updatePasswordForgot) {
                // Connexion automatique : Sauvegarder le token et les données utilisateur
                const { token, ...userData } = data.updatePasswordForgot;
                localStorage.setItem('authToken', token);
                localStorage.setItem('authUser', JSON.stringify(userData));

                showSnackbar(
                    'Réinitialisation du mot de passe réussie ! Vous êtes maintenant connecté.',
                    'success'
                );

                // Naviguer vers le tableau de bord
                navigate('/dashboard');
            }
        },
        onError: (error) => {
            const errorMessage = error.message;
            if (errorMessage.includes('invalid') || errorMessage.includes('invalide')) {
                showSnackbar(
                    'Code de vérification invalide ou expiré. Veuillez vérifier et réessayer.',
                    'error'
                );
            } else {
                showSnackbar(
                    errorMessage || 'Échec de la réinitialisation du mot de passe. Veuillez réessayer.',
                    'error'
                );
            }
        },
    });

    const [resendCode, { loading: resendingCode }] = useMutation(RESEND_RESET_PASSWORD_CODE, {
        onCompleted: () => {
            showSnackbar('Nouveau code de vérification envoyé à votre e-mail', 'success');
        },
        onError: (error) => {
            showSnackbar(
                error.message || 'Échec de l\'envoi du code. Veuillez réessayer.',
                'error'
            );
        },
    });

    useEffect(() => {
        // Vérifier si les mots de passe correspondent
        if (confirmPassword) {
            setPasswordsMatch(newPassword === confirmPassword);
        }
    }, [newPassword, confirmPassword]);

    const validatePassword = (password: string): boolean => {
        // Exigences du mot de passe :
        // - Au moins 8 caractères
        // - Contient une lettre majuscule
        // - Contient une lettre minuscule
        // - Contient un chiffre
        // - Contient un caractère spécial
        const minLength = password.length >= 8;
        const hasUppercase = /[A-Z]/.test(password);
        const hasLowercase = /[a-z]/.test(password);
        const hasNumber = /\d/.test(password);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        return minLength && hasUppercase && hasLowercase && hasNumber && hasSpecialChar;
    };

    const handleSubmit = async () => {
        // Valider le formulaire
        if (!email.trim()) {
            showSnackbar('Veuillez entrer votre adresse e-mail', 'warning');
            return;
        }

        if (!code.trim()) {
            showSnackbar('Veuillez entrer le code de vérification', 'warning');
            return;
        }

        if (code.trim().length !== 6) {
            showSnackbar('Le code de vérification doit contenir 6 caractères', 'warning');
            return;
        }

        if (!newPassword) {
            showSnackbar('Veuillez entrer un nouveau mot de passe', 'warning');
            return;
        }

        if (!validatePassword(newPassword)) {
            showSnackbar('Le mot de passe ne respecte pas les exigences', 'warning');
            return;
        }

        if (newPassword !== confirmPassword) {
            showSnackbar('Les mots de passe ne correspondent pas', 'warning');
            return;
        }

        try {
            await updatePasswordForgot({
                variables: {
                    resetPasswordInput: {
                        email: email.trim().toLowerCase(),
                        code: code.trim().toUpperCase(),
                        newPassword: newPassword,
                    },
                },
            });
        } catch (err) {
            // Erreur gérée par le callback onError
        }
    };

    const handleResendCode = async () => {
        if (!email.trim()) {
            showSnackbar('Veuillez entrer votre adresse e-mail', 'warning');
            return;
        }

        try {
            await resendCode({
                variables: { email: email.trim().toLowerCase() },
            });
        } catch (err) {
            // Erreur gérée par le callback onError
        }
    };

    const isFormValid =
        email.trim().length > 0 &&
        code.trim().length === 6 &&
        newPassword.length > 0 &&
        validatePassword(newPassword) &&
        passwordsMatch;

    return (
        <>
            <PageHead useDynamicTitle />
            <Box className="reset-password-container">
                {/* Icône de clé avec lueur violette */}
                <Box className="key-icon-wrapper">
                    <i className='fi fi-br-key' style={{ fontSize: '24px' }}></i>
                </Box>

                {/* Titre et sous-titre */}
                <Box className="header-section">
                    <Typography variant="h1" className="main-heading">
                        Réinitialisez votre mot de passe
                    </Typography>
                    <Typography variant="body1" className="subtitle">
                        {email
                            ? `Entrez le code de vérification envoyé à ${email} et choisissez un nouveau mot de passe.`
                            : 'Entrez le code de vérification envoyé à votre e-mail et choisissez un nouveau mot de passe.'
                        }
                    </Typography>
                </Box>

                {/* Formulaire */}
                <form className="reset-form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                    {/* Champ code de vérification */}
                    <Box className="input-group">
                        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                            <InputOTP
                                value={code}
                                onChange={setCode}
                                disabled={resettingPassword}
                                length={6}
                            />
                        </Box>


                        {/* Bouton renvoyer le code */}
                        <Button
                            onClick={handleResendCode}
                            disabled={resendingCode || resettingPassword || !email.trim()}
                            variant="text"
                            color="primary"
                        >
                            {resendingCode ? 'Envoi en cours...' : 'Renvoyer le code'}
                        </Button>
                    </Box>


                    {/* Champ nouveau mot de passe */}
                    <Box className="input-group">
                        <PasswordInput
                            label={"Nouveau mot de passe"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Entrez votre nouveau mot de passe"
                            className="custom-input"
                            disabled={resettingPassword}
                            error={newPassword.length > 0 && !validatePassword(newPassword)}
                            helperText={
                                newPassword.length > 0 && !validatePassword(newPassword)
                                    ? 'Le mot de passe ne respecte pas les exigences'
                                    : ''
                            }
                            fullWidth
                        />
                    </Box>

                    {/* Champ confirmation mot de passe */}
                    <Box className="input-group">
                        <PasswordInput
                            label={"Confirmer le mot de passe"}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirmez votre nouveau mot de passe"
                            className="custom-input"
                            disabled={resettingPassword}
                            error={confirmPassword.length > 0 && !passwordsMatch}
                            helperText={
                                confirmPassword.length > 0 && !passwordsMatch
                                    ? 'Les mots de passe ne correspondent pas'
                                    : ''
                            }
                            fullWidth
                        />
                    </Box>

                    {/* Exigences du mot de passe */}
                    <Box className="password-requirements">
                        <Typography variant="body2" className="requirements-title">
                            Exigences du mot de passe :
                        </Typography>
                        <ul className="requirements-list">
                            <li className={newPassword.length >= 8 ? 'valid' : 'invalid'}>
                                Au moins 8 caractères
                            </li>
                            <li className={/[A-Z]/.test(newPassword) ? 'valid' : 'invalid'}>
                                Une lettre majuscule
                            </li>
                            <li className={/[a-z]/.test(newPassword) ? 'valid' : 'invalid'}>
                                Une lettre minuscule
                            </li>
                            <li className={/\d/.test(newPassword) ? 'valid' : 'invalid'}>
                                Un chiffre
                            </li>
                            <li className={/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? 'valid' : 'invalid'}>
                                Un caractère spécial
                            </li>
                        </ul>
                    </Box>

                    {/* Bouton de soumission */}
                    <button
                        type="submit"
                        disabled={!isFormValid || resettingPassword}
                        className="submit-button"
                    >
                        {resettingPassword ? 'Réinitialisation en cours...' : 'Réinitialiser le mot de passe'}
                    </button>
                </form>

                {/* Avis de sécurité */}
                <Typography variant="body2" className="security-notice">
                    Pour des raisons de sécurité, nous ne stockons PAS votre mot de passe. Soyez assuré que nous ne vous enverrons jamais votre mot de passe par e-mail. Pour plus d'informations, vous pouvez nous contacter à{' '}
                    <a href="mailto:support@upercut.fr" className="support-link">
                        support@upercut.fr
                    </a>
                    .
                </Typography>
            </Box>
        </>
    );
};
